import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CAlert,
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CForm,
  CFormInput,
  CFormSelect,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CPagination,
  CPaginationItem,
  CRow,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import { DEFAULT_PAGE_SIZE, useMantenimientoFlota } from './hooks/useMantenimientoFlota.js';

const ESTADOS = [
  { value: '', label: 'Todos' },
  { value: 'ACTIVO', label: 'Activo' },
  { value: 'INACTIVO', label: 'Inactivo' },
];

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString();
}

function EstadoBadge({ estado }) {
  const color = estado === 'ACTIVO' ? 'success' : 'secondary';
  return <CBadge color={color}>{estado}</CBadge>;
}

export default function ListadoMantenimientoFlota() {
  const navigate = useNavigate();
  const { sucursal } = useAuth();
  const { loading, error, permissions, list, remove } = useMantenimientoFlota();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    estado: '',
    sucursalId: sucursal?.id ? String(sucursal.id) : '',
  });
  const [pendingDelete, setPendingDelete] = useState(null);

  const totalPages = useMemo(() => {
    const total = data?.totalCount || 0;
    return Math.max(1, Math.ceil(total / pageSize));
  }, [data, pageSize]);

  useEffect(() => {
    if (!permissions.canView) return;
    const fetchData = async () => {
      const filter = {
        search: filters.search || null,
        estado: filters.estado || null,
        sucursalId: filters.sucursalId || null,
      };
      const result = await list({ page, pageSize, filter });
      setData(result);
    };

    fetchData();
  }, [filters, list, page, pageSize, permissions.canView]);

  const handleFilterChange = event => {
    const { name, value } = event.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitFilters = event => {
    event.preventDefault();
    setPage(1);
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    const ok = await remove(pendingDelete.id);
    if (ok) {
      setPendingDelete(null);
      const result = await list({
        page: 1,
        pageSize,
        filter: {
          search: filters.search || null,
          estado: filters.estado || null,
          sucursalId: filters.sucursalId || null,
        },
      });
      setPage(1);
      setData(result);
    }
  };

  if (!permissions.canView) {
    return (
      <CAlert color="danger" className="m-4">
        No tenés permisos para acceder al módulo de Mantenimiento de Flota.
      </CAlert>
    );
  }

  return (
    <div className="p-4">
      <CCard className="mb-4">
        <CCardHeader className="d-flex justify-content-between align-items-center">
          <div>
            <h4 className="mb-1">Mantenimiento de Flota</h4>
            <p className="mb-0 text-medium-emphasis">
              Gestión de trabajos preventivos y correctivos por unidad.
            </p>
          </div>
          {permissions.canCreate && (
            <CButton color="primary" onClick={() => navigate('/mantenimiento-flota/nuevo')}>
              Nuevo mantenimiento
            </CButton>
          )}
        </CCardHeader>
        <CCardBody>
          <CForm onSubmit={handleSubmitFilters} className="mb-3">
            <CRow className="g-3">
              <CCol xs={12} md={5}>
                <CFormInput
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                  placeholder="Buscar por unidad, patente o descripción"
                  label="Búsqueda"
                />
              </CCol>
              <CCol xs={12} md={3}>
                <CFormSelect
                  name="estado"
                  label="Estado"
                  value={filters.estado}
                  onChange={handleFilterChange}
                  options={ESTADOS}
                />
              </CCol>
              <CCol xs={12} md={2}>
                <CFormInput
                  name="sucursalId"
                  value={filters.sucursalId}
                  onChange={handleFilterChange}
                  placeholder="ID sucursal"
                  label="Sucursal"
                />
              </CCol>
              <CCol xs={12} md={2} className="d-flex align-items-end">
                <CButton color="secondary" type="submit" className="w-100">
                  Aplicar
                </CButton>
              </CCol>
            </CRow>
          </CForm>

          {error && <CAlert color="danger">{error}</CAlert>}

          {loading ? (
            <div className="d-flex align-items-center gap-2">
              <CSpinner size="sm" />
              <span>Cargando mantenimientos...</span>
            </div>
          ) : null}

          {!loading && data?.edges?.length === 0 && (
            <CAlert color="info" className="mt-3">
              No hay mantenimientos registrados con los filtros seleccionados.
            </CAlert>
          )}

          {data?.edges?.length ? (
            <CTable hover responsive className="mt-3">
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell>Unidad</CTableHeaderCell>
                  <CTableHeaderCell>Patente</CTableHeaderCell>
                  <CTableHeaderCell>Descripción</CTableHeaderCell>
                  <CTableHeaderCell>Programado</CTableHeaderCell>
                  <CTableHeaderCell>Estado</CTableHeaderCell>
                  <CTableHeaderCell>Sucursal</CTableHeaderCell>
                  <CTableHeaderCell className="text-end">Acciones</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {data.edges.map(({ node }) => (
                  <CTableRow key={node.id}>
                    <CTableDataCell>{node.unidad}</CTableDataCell>
                    <CTableDataCell>{node.patente || '—'}</CTableDataCell>
                    <CTableDataCell>{node.descripcion || '—'}</CTableDataCell>
                    <CTableDataCell>{formatDate(node.fechaProgramada)}</CTableDataCell>
                    <CTableDataCell>
                      <EstadoBadge estado={node.estado} />
                    </CTableDataCell>
                    <CTableDataCell>{node.sucursalId}</CTableDataCell>
                    <CTableDataCell className="text-end">
                      {permissions.canEdit && (
                        <CButton
                          color="warning"
                          variant="outline"
                          size="sm"
                          className="me-2"
                          onClick={() => navigate(`/mantenimiento-flota/${node.id}/editar`)}
                        >
                          Editar
                        </CButton>
                      )}
                      {permissions.canDelete && (
                        <CButton
                          color="danger"
                          variant="outline"
                          size="sm"
                          onClick={() => setPendingDelete(node)}
                        >
                          Dar de baja
                        </CButton>
                      )}
                    </CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
          ) : null}

          {totalPages > 1 && (
            <CPagination className="mt-3">
              <CPaginationItem
                disabled={page === 1}
                onClick={() => setPage(prev => Math.max(prev - 1, 1))}
              >
                Anterior
              </CPaginationItem>
              {Array.from({ length: totalPages }, (_, index) => {
                const pageNumber = index + 1;
                return (
                  <CPaginationItem
                    key={pageNumber}
                    active={pageNumber === page}
                    onClick={() => setPage(pageNumber)}
                  >
                    {pageNumber}
                  </CPaginationItem>
                );
              })}
              <CPaginationItem
                disabled={page === totalPages}
                onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
              >
                Siguiente
              </CPaginationItem>
            </CPagination>
          )}
        </CCardBody>
      </CCard>

      <CModal visible={Boolean(pendingDelete)} onClose={() => setPendingDelete(null)}>
        <CModalHeader>
          <CModalTitle>Confirmar baja</CModalTitle>
        </CModalHeader>
        <CModalBody>
          ¿Querés dar de baja el mantenimiento de la unidad{' '}
          <strong>{pendingDelete?.unidad}</strong>?
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={() => setPendingDelete(null)}>
            Cancelar
          </CButton>
          <CButton color="danger" onClick={handleDelete} disabled={loading}>
            Confirmar baja
          </CButton>
        </CModalFooter>
      </CModal>
    </div>
  );
}
